import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { startOfWeek, endOfWeek, addWeeks, format } from 'date-fns';
import MealPlanResults from './MealPlanResults';
import { trackMealPlanCreated } from '../utils/analytics';
import { SpiceLevelPicker } from './MenuPreferencesFields';
import './MenuPreferencesFields.css';
import './MealPlanifyModal.css';

const MealPlanifyModal = ({ isOpen, onClose, onMealPlanGenerated, inline = false }) => {
  const [preferences, setPreferences] = useState({
    dietaryPreferences: [],
    allergies: [],
    costRange: 'medium',
    mealsToConsider: ['breakfast', 'lunch', 'dinner'],
    daysToPlan: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
    selectedWeeks: [0], // Array of week indices (0 = this week, 1 = next week, etc.)
    cuisinePreferences: [],
    spiceLevel: 3,
    mealPrepRecipes: false,
    lowCarb: false,
    highProtein: false,
    mealVariety: 'medium', // high = all different, medium = some repeats ok, low = can repeat multiple days, none = same meal every day
    proteinPreferences: [] // Fish, Shellfish, Poultry, Beef, Pork, Lamb
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const [savedPreferences, setSavedPreferences] = useState(null);
  const [generationResults, setGenerationResults] = useState(null);
  const [dietaryOptions, setDietaryOptions] = useState([]);
  const [cuisineOptions, setCuisineOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  // Load menu options on mount
  useEffect(() => {
    loadMenuOptions();
  }, []);

  // Load saved preferences when opened
  useEffect(() => {
    if (isOpen) {
      loadSavedPreferences();
    }
  }, [isOpen]);

  // For inline mode, load preferences on mount
  useEffect(() => {
    if (inline) {
      loadSavedPreferences();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMenuOptions = async () => {
    try {
      setOptionsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/menu-options/options`);
      
      if (response.ok) {
        const data = await response.json();
        
        // Convert dietary options to title case for display
        const formattedDietary = data.dietary.map(diet => {
          // Convert kebab-case and lowercase to Title Case
          return diet
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join('-');
        });
        
        setDietaryOptions(formattedDietary);
        setCuisineOptions(data.cuisine);
        
        console.log('Loaded menu options:', {
          dietary: formattedDietary.length,
          cuisine: data.cuisine.length
        });
      } else {
        console.error('Failed to load menu options');
        // Fallback to empty arrays if fetch fails
        setDietaryOptions([]);
        setCuisineOptions([]);
      }
    } catch (error) {
      console.error('Error loading menu options:', error);
      // Fallback to empty arrays on error
      setDietaryOptions([]);
      setCuisineOptions([]);
    } finally {
      setOptionsLoading(false);
    }
  };

  const loadSavedPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/mealplan/preferences`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.preferences) {
          setPreferences(prev => ({ ...prev, ...data.preferences }));
          setSavedPreferences(data.preferences);
        }
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const savePreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/mealplan/preferences`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ preferences })
      });

      if (!response.ok) {
        throw new Error('Failed to save preferences');
      }

      toast.success('Preferences saved successfully!');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    }
  };

  const handlePreferenceChange = (category, value) => {
    setPreferences(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleArrayPreferenceChange = (category, value) => {
    setPreferences(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(item => item !== value)
        : [...prev[category], value]
    }));
  };

  // Generate week options with date ranges
  const getWeekOptions = () => {
    const today = new Date();
    const weeks = [];
    
    for (let i = 0; i < 4; i++) {
      const weekStart = startOfWeek(addWeeks(today, i), { weekStartsOn: 0 }); // Sunday
      const weekEnd = endOfWeek(addWeeks(today, i), { weekStartsOn: 0 }); // Saturday
      
      weeks.push({
        index: i,
        label: i === 0 ? 'This Week' : `Week ${i + 1}`,
        dateRange: `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d')}`,
        startDate: weekStart,
        endDate: weekEnd
      });
    }
    
    return weeks;
  };

  const handleGenerateMealPlan = async () => {
    if (preferences.daysToPlan.length === 0) {
      toast.error('Please select at least one day to plan');
      return;
    }

    if (preferences.mealsToConsider.length === 0) {
      toast.error('Please select at least one meal type');
      return;
    }

    if (preferences.selectedWeeks.length === 0) {
      toast.error('Please select at least one week to plan');
      return;
    }

    setIsGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/mealplan/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ preferences })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate meal plan');
      }

      const result = await response.json();
      toast.success('Meal plan generated successfully!');
      trackMealPlanCreated('meal_planify');

      // Save preferences for future use
      await savePreferences();
      
      // Show results
      setGenerationResults(result);
      
    } catch (error) {
      console.error('Error generating meal plan:', error);
      toast.error(error.message || 'Failed to generate meal plan');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCloseResults = () => {
    setGenerationResults(null);
    onClose();
    onMealPlanGenerated(generationResults);
  };

  if (!inline && !isOpen) return null;

  // Show results if generation was successful
  if (generationResults) {
    return (
      <MealPlanResults 
        results={generationResults}
        onClose={handleCloseResults}
      />
    );
  }

  // Allergy options remain hardcoded as they're not in the database
  const allergyOptions = [
    'Nuts', 'Peanuts', 'Shellfish', 'Fish', 'Eggs', 'Soy', 'Wheat', 
    'Dairy', 'Sesame', 'Sulfites'
  ];

  const dayOptions = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  const mealOptions = [
    { value: 'breakfast', label: 'Breakfast' },
    { value: 'lunch', label: 'Lunch' },
    { value: 'dinner', label: 'Dinner' },
    { value: 'snack', label: 'Snack' }
  ];

  const preferencesContent = (
    <>
      <div className="preferences-section">
        <h3>Dietary Preferences</h3>
        {optionsLoading ? (
          <div className="loading-options">Loading dietary options...</div>
        ) : dietaryOptions.length === 0 ? (
          <div className="no-options">No dietary options available</div>
        ) : (
          <div className="preference-grid">
            {dietaryOptions.map(option => (
              <label key={option} className="preference-checkbox">
                <input
                  type="checkbox"
                  checked={preferences.dietaryPreferences.includes(option.toLowerCase().replace(/\s+/g, '-'))}
                  onChange={() => handleArrayPreferenceChange('dietaryPreferences', option.toLowerCase().replace(/\s+/g, '-'))}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="preferences-section">
        <h3>Allergies</h3>
        <div className="preference-grid">
          {allergyOptions.map(option => (
            <label key={option} className="preference-checkbox">
              <input
                type="checkbox"
                checked={preferences.allergies.includes(option.toLowerCase())}
                onChange={() => handleArrayPreferenceChange('allergies', option.toLowerCase())}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="preferences-section">
        <h3>Cuisine Preferences</h3>
        {optionsLoading ? (
          <div className="loading-options">Loading cuisine options...</div>
        ) : cuisineOptions.length === 0 ? (
          <div className="no-options">No cuisine options available</div>
        ) : (
          <div className="preference-grid">
            {cuisineOptions.map(option => (
              <label key={option} className="preference-checkbox">
                <input
                  type="checkbox"
                  checked={preferences.cuisinePreferences.includes(option.toLowerCase())}
                  onChange={() => handleArrayPreferenceChange('cuisinePreferences', option.toLowerCase())}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="preferences-section">
        <h3>Protein Preferences</h3>
        <div className="preference-grid">
          {['Fish', 'Shellfish', 'Poultry', 'Beef', 'Pork', 'Lamb'].map(option => (
            <label key={option} className="preference-checkbox">
              <input
                type="checkbox"
                checked={preferences.proteinPreferences.includes(option.toLowerCase())}
                onChange={() => handleArrayPreferenceChange('proteinPreferences', option.toLowerCase())}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="preferences-section">
        <h3>Cost Range</h3>
        <div className="cost-options">
          {[
            { value: 'low', label: 'Budget-Friendly', icon: '💰' },
            { value: 'medium', label: 'Moderate', icon: '💳' },
            { value: 'high', label: 'Premium', icon: '💎' }
          ].map(option => (
            <label key={option.value} className="cost-option">
              <input
                type="radio"
                name="costRange"
                value={option.value}
                checked={preferences.costRange === option.value}
                onChange={(e) => handlePreferenceChange('costRange', e.target.value)}
              />
              <div className="cost-option-content">
                <span className="cost-icon">{option.icon}</span>
                <span>{option.label}</span>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="preferences-section">
        <h3>Meals to Include</h3>
        <div className="meal-options">
          {mealOptions.map(option => (
            <label key={option.value} className="meal-option">
              <input
                type="checkbox"
                checked={preferences.mealsToConsider.includes(option.value)}
                onChange={() => handleArrayPreferenceChange('mealsToConsider', option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="preferences-section">
        <h3>Days to Plan</h3>
        <div className="day-options">
          {dayOptions.map(option => (
            <label key={option.value} className="day-option">
              <input
                type="checkbox"
                checked={preferences.daysToPlan.includes(option.value)}
                onChange={() => handleArrayPreferenceChange('daysToPlan', option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="preferences-section">
        <h3>Weeks to Plan</h3>
        <div className="weeks-selector">
          {getWeekOptions().map(week => (
            <label key={week.index} className="week-option">
              <input
                type="checkbox"
                checked={preferences.selectedWeeks.includes(week.index)}
                onChange={() => handleArrayPreferenceChange('selectedWeeks', week.index)}
              />
              <div className="week-option-content">
                <span className="week-label">{week.label}</span>
                <span className="week-dates">{week.dateRange}</span>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="preferences-section">
        <h3>Additional Options</h3>
        <div className="additional-options">
          <label className="additional-option">
            <input
              type="checkbox"
              checked={preferences.mealPrepRecipes}
              onChange={(e) => handlePreferenceChange('mealPrepRecipes', e.target.checked)}
            />
            <span>Prefer Meal Prep Recipes</span>
          </label>
          <label className="additional-option">
            <input
              type="checkbox"
              checked={preferences.lowCarb}
              onChange={(e) => handlePreferenceChange('lowCarb', e.target.checked)}
            />
            <span>Low Carb Focus</span>
          </label>
          <label className="additional-option">
            <input
              type="checkbox"
              checked={preferences.highProtein}
              onChange={(e) => handlePreferenceChange('highProtein', e.target.checked)}
            />
            <span>High Protein Focus</span>
          </label>
        </div>
      </div>

      <div className="preferences-section">
        <h3>Meal Variety</h3>
        <div className="variety-options">
          <label className="variety-option">
            <input
              type="radio"
              name="mealVariety"
              value="high"
              checked={preferences.mealVariety === 'high'}
              onChange={(e) => handlePreferenceChange('mealVariety', e.target.value)}
            />
            <div className="variety-option-content">
              <span className="variety-label">High Variety</span>
              <span className="variety-description">All different meals each day</span>
            </div>
          </label>
          <label className="variety-option">
            <input
              type="radio"
              name="mealVariety"
              value="medium"
              checked={preferences.mealVariety === 'medium'}
              onChange={(e) => handlePreferenceChange('mealVariety', e.target.value)}
            />
            <div className="variety-option-content">
              <span className="variety-label">Medium Variety</span>
              <span className="variety-description">Can repeat 2-3 times per week</span>
            </div>
          </label>
          <label className="variety-option">
            <input
              type="radio"
              name="mealVariety"
              value="low"
              checked={preferences.mealVariety === 'low'}
              onChange={(e) => handlePreferenceChange('mealVariety', e.target.value)}
            />
            <div className="variety-option-content">
              <span className="variety-label">Low Variety</span>
              <span className="variety-description">Can repeat same meal multiple days</span>
            </div>
          </label>
          <label className="variety-option">
            <input
              type="radio"
              name="mealVariety"
              value="none"
              checked={preferences.mealVariety === 'none'}
              onChange={(e) => handlePreferenceChange('mealVariety', e.target.value)}
            />
            <div className="variety-option-content">
              <span className="variety-label">No Variety</span>
              <span className="variety-description">Same meals every day</span>
            </div>
          </label>
        </div>
      </div>

      <div className="preferences-section">
        <h3>Spice Level</h3>
        <SpiceLevelPicker
          value={preferences.spiceLevel}
          onChange={(level) => handlePreferenceChange('spiceLevel', level)}
          disabled={isGenerating}
        />
      </div>
    </>
  );
  if (inline) {
    return (
      <div className="meal-planify-inline">
        <div className="meal-planify-inline-header">
          <div className="inline-title-row">
            <h2><i className="fas fa-magic"></i> MealPlanify</h2>
            <p className="inline-subtitle">Generate a personalized meal plan from your preferences</p>
          </div>
          <button className="meal-planify-inline-close" onClick={onClose}>
            <i className="fas fa-times"></i> Cancel
          </button>
        </div>

        {preferencesContent}

        <div className="meal-planify-inline-footer">
          <button
            className="save-preferences-btn"
            onClick={savePreferences}
            disabled={isGenerating}
          >
            <i className="fas fa-save"></i>
            Save Preferences
          </button>
          <button
            className="generate-meal-plan-btn"
            onClick={handleGenerateMealPlan}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <><i className="fas fa-spinner fa-spin"></i> Generating...</>
            ) : (
              <><i className="fas fa-magic"></i> Generate Meal Plan</>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="meal-planify-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="meal-planify-modal">
        <div className="modal-header">
          <h2>
            <i className="fas fa-magic"></i>
            MealPlanify
          </h2>
          <button className="close-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-content">
          <div className="mealplanify-intro">
            <div className="intro-icon">
              <i className="fas fa-wand-magic-sparkles"></i>
            </div>
            <div className="intro-text">
              <p className="intro-headline">Let us build your perfect meal plan!</p>
              <p className="intro-description">
                Tell us your dietary preferences, allergies, and favorite cuisines below. 
                We'll automatically generate a personalized meal plan using recipes from our curated menu collection, 
                tailored just for you.
              </p>
            </div>
          </div>

          {preferencesContent}
        </div>

        <div className="modal-footer">
          <button 
            className="save-preferences-btn"
            onClick={savePreferences}
            disabled={isGenerating}
          >
            <i className="fas fa-save"></i>
            Save Preferences
          </button>
          <button 
            className="generate-meal-plan-btn"
            onClick={handleGenerateMealPlan}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Generating...
              </>
            ) : (
              <>
                <i className="fas fa-magic"></i>
                Generate Meal Plan
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MealPlanifyModal;
